import { useEffect, useState } from 'react'

import { DEBOUNCE_TIMER_300 } from 'src/Constants/constants'
import { useAppDispatch } from 'src/Hooks/redux'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import type { OrderItem } from 'src/Redux/artistOrderApi'

type ResolvableRequest = { unwrap: () => Promise<unknown> }

type UseOrderBudgetSyncParams = {
  enabled: boolean
  isOrderMutable: boolean
  budget: number
  orderId?: number
  campaignItem?: OrderItem
  coupons: string[]
  updateOrderItem: (body: {
    orderId: number
    itemId: number
    fullPrice: number
  }) => ResolvableRequest
  triggerCheckoutInfo: (body: { coupons: string[] }) => ResolvableRequest
}

export const useOrderBudgetSync = ({
  enabled,
  isOrderMutable,
  budget,
  orderId,
  campaignItem,
  coupons,
  updateOrderItem,
  triggerCheckoutInfo,
}: UseOrderBudgetSyncParams): boolean => {
  const dispatch = useAppDispatch()
  const [isRequestPending, setIsRequestPending] = useState(false)
  const [failedBudget, setFailedBudget] = useState<number | null>(null)
  const couponsKey = coupons.join(',')
  const itemId = campaignItem?.id
  const itemPrice = campaignItem?.fullPrice
  const hasTarget = orderId !== void 0 && itemId !== void 0
  const isSyncAllowed = enabled && isOrderMutable && hasTarget
  const isBudgetChanged = itemPrice !== budget && budget > 0
  const shouldSync = isSyncAllowed && isBudgetChanged
  const isSyncing = isRequestPending || (shouldSync && failedBudget !== budget)

  useEffect(() => {
    const timer = shouldSync
      ? setTimeout(() => {
          const recalculate = async (): Promise<void> => {
            setIsRequestPending(true)
            await updateOrderItem({
              orderId: Number(orderId),
              itemId: Number(itemId),
              fullPrice: budget,
            }).unwrap()
            await triggerCheckoutInfo({
              coupons: couponsKey ? couponsKey.split(',') : [],
            }).unwrap()
          }

          recalculate()
            .catch(() => {
              setFailedBudget(budget)
              dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
            })
            .finally(() => setIsRequestPending(false))
        }, DEBOUNCE_TIMER_300)
      : null

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [
    budget,
    couponsKey,
    dispatch,
    itemId,
    orderId,
    shouldSync,
    triggerCheckoutInfo,
    updateOrderItem,
  ])

  return isSyncing
}
